<template>
    <dialog class="color-picker-dialog" ref="colorPicker">
        <div class="overlay"></div>
        <div role="application" aria-label="PhotoShop color picker" :class="['vc-photoshop', disableFields ? 'vc-photoshop__disable-fields' : '']">
            <div role="heading" class="vc-ps-head">{{head}}</div>

            <div class="vc-tabs-container" v-if="!disablePalette">
                <div class="vc-tabs">
                    <div class="btn btn-sm" @click="tab = `sliders`" :class="tab === `sliders` ? `btn-primary`: `btn-white`" v-tr>Sliders|Personnalisé</div>
                    <div class="btn btn-sm" @click="tab = `palette`" :class="tab === `palette` ? `btn-primary`: `btn-white`" v-tr>Palette</div>
                </div>
            </div>

            <div class="vc-ps-body">
                <template v-if="tab === `sliders`">
                    <div class="vc-ps-saturation-wrap">
                        <saturation-slider v-model="colors" @change="childChange"/>

                        <div class="vc-ps-alpha-wrap" v-if="!disableAlpha">
                            <alpha-slider v-model="colors" @change="childChange">
                                <div class="vc-ps-hue-pointer">
                                    <i class="vc-ps-hue-pointer--left"></i><i class="vc-ps-hue-pointer--right"></i>
                                </div>
                            </alpha-slider>
                        </div>
                    </div>
                    <div class="vc-ps-hue-wrap">
                        <hue-slider v-model="colors" @change="childChange" direction="vertical">
                            <div class="vc-ps-hue-pointer">
                                <i class="vc-ps-hue-pointer--left"></i><i class="vc-ps-hue-pointer--right"></i>
                            </div>
                        </hue-slider>

                        <div v-if="!disableAlpha" class="vc-transparent-wrap" title="Transparent" @click="makeTransparent">
                            <checkerboard/>
                        </div>
                    </div>
                </template>
                <div v-else-if="tab === `palette`" class="vc-sketch-presets" role="group" aria-label="A color preset, pick one to set as current color">
                    <template v-for="c in presets">
                        <div v-if="!isTransparent(c)" class="vc-sketch-presets-color" :aria-label="'Color:' + c" :key="c" :style="{background: c}" @click="handlePreset(c)"/>
                        <div v-else :key="c" :aria-label="'Color:' + c" class="vc-sketch-presets-color" @click="handlePreset(c)">
                            <checkerboard/>
                        </div>
                    </template>
                </div>
                <div :class="['vc-ps-controls', disableFields ? 'vc-ps-controls__disable-fields' : '']">
                    <div class="vc-ps-previews">
                        <div class="vc-ps-previews__label">{{ newLabel }}</div>
                        <div class="vc-ps-previews__swatches">
                            <div class="vc-ps-previews__pr-color" :aria-label="`New color is ${colors.hex}`" :style="{background: activeColor}"></div>
                            <div class="vc-ps-previews__pr-color" :aria-label="`Current color is ${currentColor}`" :style="{background: currentColor}" @click="clickCurrentColor"></div>
                            <checkerboard size="16"></checkerboard>
                        </div>
                        <div class="vc-ps-previews__label">{{ currentLabel }}</div>
                    </div>
                    <div class="vc-ps-actions" v-if="!disableFields">
                        <div class="btn btn-sm" :class="`btn-${okButtonVariant}`" role="button" :aria-label="acceptLabel" @click="handleAccept">{{ acceptLabel }}</div>
                        <div class="btn btn-sm" :class="`btn-${cancelButtonVariant}`" role="button" :aria-label="cancelLabel" @click="handleCancel">{{ cancelLabel }}</div>

                        <div class="vc-ps-fields">
                            <!-- hsla -->
                            <editable-input label="h" desc="°" :value="hsv.h" @change="inputChange"/>
                            <editable-input label="s" desc="%" :value="hsv.s" :max="100" @change="inputChange"/>
                            <editable-input label="v" desc="%" :value="hsv.v" :max="100" @change="inputChange"/>
                            <div class="vc-ps-fields__divider"></div>
                            <!-- rgba -->
                            <editable-input label="r" :value="colors.rgba.r" @change="inputChange"/>
                            <editable-input label="g" :value="colors.rgba.g" @change="inputChange"/>
                            <editable-input label="b" :value="colors.rgba.b" @change="inputChange"/>
                            <editable-input label="a" v-if="!disableAlpha" :value="colors.rgba.a" :arrow-offset="0.01" :max="1" @change="inputChange"/>
                            <div class="vc-ps-fields__divider"></div>
                            <!-- hex -->
                            <editable-input class="vc-ps-fields__hex" label="#" :value="hex" @enter="handleAccept" autofocus @change="inputChange"/>
                            <!-- (note that label is really important for inputChange) -->
                        </div>

                        <div v-if="hasResetButton" class="vc-ps-ac-btn" aria-label="reset" @click="handleReset">{{ resetLabel }}</div>
                    </div>
                </div>
            </div>
        </div>
    </dialog>
</template>

<script>
/* eslint-disable */
/* Initially forked from vue-color 2.8 to add an alpha channel */

import colorMixin from './mixin/color-picker-mixin.js'
import ColorEditableInput from './common/color-editable-input.vue'
import ColorSaturationSlider from './common/color-saturation-slider.vue'
import ColorHueSlider from './common/color-hue-slider.vue'
import ColorAlphaSlider from './common/color-alpha-slider.vue'
import ColorCheckerboardBg from './common/color-checkerboard-bg.vue'
import tinycolor from "tinycolor2";

export default {
    name: `e-photoshop-color-picker`,
    mixins: [colorMixin],
    props: {
        head: { type: String, default: `Color Picker` },
        disableFields: { type: Boolean, default: false },
        hasResetButton: { type: Boolean, default: false },
        acceptLabel: { type: String, default: `OK` },
        cancelLabel: { type: String, default: `Cancel` },
        resetLabel: { type: String, default: `Reset` },
        newLabel: { type: String, default: `new` },
        okButtonVariant: { type: String, default: `primary` },
        cancelButtonVariant: { type: String, default: `white` },
        currentLabel: { type: String, default: `current` },
        disableAlpha: { type: Boolean, default: false },
        disablePalette: { type: Boolean, default: false },
        visible: { type: Boolean, default: false },
        presets: {type: Array, default: () => [
            "#ffffff", "#f3f3f3", "#efefef", "#d9d9d9", "#cccccc", "#b7b7b7", "#999999", "#666666", "#434343", "#000000",
            "#f4cccc", "#e6b8af", "#fce5cd", "#fff2cc", "#d9ead3", "#c5dee2", "#c9daf8", "#cfe2f3", "#d9d2e9", "#ead1dc",
            "#ea9999", "#dd7e6b", "#f9cb9c", "#ffe599", "#b6d7a8", "#a2c4c9", "#a4c2f4", "#9fc5e8", "#b4a7d6", "#d5a6bd",
            "#e06666", "#cc4125", "#f6b26b", "#ffd966", "#93c47d", "#76a5af", "#6d9eeb", "#6fa8dc", "#8e7cc3", "#c27ba0",
            "#cc0000", "#a61c00", "#e69138", "#f1c232", "#6aa84f", "#45818e", "#3c78d8", "#3d85c6", "#674ea7", "#a64d79",
            "#990000", "#85200c", "#b45f06", "#bf9000", "#38761d", "#134f5c", "#1155cc", "#0b5394", "#351c75", "#741b47",
            "#660000", "#5b0f00", "#783f04", "#7f6000", "#274e13", "#0c343d", "#1c4587", "#073763", "#20124d", "#4c1130",
            "#ff0000", "#980000", "#ff9900", "#ffff00", "#00ff00", "#00d2f7", "#4a86e8", "#0000ff", "#9900ff", "#d84089"
        ]}
    },
    components: {
        saturationSlider: ColorSaturationSlider,
        hueSlider: ColorHueSlider,
        alphaSlider: ColorAlphaSlider,
        checkerboard: ColorCheckerboardBg,
        editableInput: ColorEditableInput
    },
    data() {
        return {
            currentColor: `#FFF`,
            tab: `sliders`
        }
    },
    computed: {
        hsv() {
            const hsv = this.colors.hsv
            return {
                h: hsv.h.toFixed(),
                s: (hsv.s * 100).toFixed(),
                v: (hsv.v * 100).toFixed()
            }
        },
        hex() {
            let hex
            if (this.colors.a < 1) {
                hex = this.colors.hex8
            } else {
                hex = this.colors.hex
            }
            return hex.replace(`#`, ``)
        },
        activeColor() {
            const rgba = this.colors.rgba
            return `rgba(` + [rgba.r, rgba.g, rgba.b, rgba.a].join(`,`) + `)`
        }
    },
    created() {
        this.currentColor = this.colors.hex
    },
    mounted() {
        this.$nextTick(() => {
            this.$refs.colorPicker.showModal();
        });
    },
    methods: {
        childChange(data) {
            this.colorChange(data)
        },
        inputChange(data) {
            if (!data) {
                return
            }

            if (data[`#`]) {
                this.isValidHex(data[`#`]) && this.colorChange({
                    hex: data[`#`],
                    source: `hex`
                })
            } else if (data.r || data.g || data.b || data.a) {
                this.colorChange({
                    r: data.r || this.colors.rgba.r,
                    g: data.g || this.colors.rgba.g,
                    b: data.b || this.colors.rgba.b,
                    a: data.a || this.colors.rgba.a,
                    source: `rgba`
                })
            } else if (data.h || data.s || data.v) {
                this.colorChange({
                    h: data.h || this.colors.hsv.h,
                    s: (data.s / 100) || this.colors.hsv.s,
                    v: (data.v / 100) || this.colors.hsv.v,
                    source: `hsv`
                })
            }
        },
        isTransparent (color) {
            return tinycolor(color).getAlpha() === 0
        },
        handlePreset(c) {
            this.colorChange({
                hex: c,
                source: `hex`
            })
        },
        clickCurrentColor() {
            this.colorChange({
                hex: this.currentColor,
                source: `hex`
            })
        },
        makeTransparent() {
            this.colorChange({
                hex: `#00000000`,
                source: `hex`
            })
        },
        show() {
            this.$emit(`update:visible`, true);
            this.$refs.colorPicker.showModal();
        },
        hide() {
            this.$emit(`update:visible`, false);
            this.$refs.colorPicker.close();
        },
        handleAccept() {
            this.$emit(`ok`, this.colors.hex);
            this.hide();
        },
        handleCancel() {
            this.$emit(`cancel`, this.currentColor);
            this.hide();
        },
        handleReset() {
            this.$emit(`reset`);
        }
    }

}
</script>

<style>
.color-picker-dialog {
    padding: 0;
    border: none;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.25);
    border-radius: 8px;
}

.vc-photoshop {
    background: #eee;
    border-radius: 8px;
    box-sizing: initial;
    width: 513px;
}
.vc-photoshop__disable-fields {
    width: 390px;
}
.vc-ps-head {
    background: #fafafa;
    border-bottom: 1px solid #B1B1B1;
    box-shadow: inset 0 1px 0 0 rgba(255,255,255,.2), inset 0 -1px 0 0 rgba(0,0,0,.02);
    height: 30px;
    line-height: 30px;
    border-radius: 4px 4px 0 0;
    font-size: 11pt;
    color: #4D4D4D;
    text-align: center;
}
.vc-ps-body {
    padding: 10px 15px 15px;
    display: flex;
}

.vc-tabs-container {
    display: flex;
    align-items: center;

    .vc-tabs {
        background: #fff;
        padding: 4px;
        border-radius: 6px;
        display: inline-flex;
        margin: 5px auto 5px;

        .btn {
            width: 150px;
            border-radius: 4px;
            height: 24px;
            line-height: 22px;
            padding: 0;
            font-size: 10pt;
        }
    }
}

.vc-ps-saturation-wrap {
    width: 256px;
    position: relative;
    overflow: hidden;

    .vc-saturation {
        border-radius: 4px;
        border: none;
        overflow: hidden;
        height: 256px;
        position: relative;
    }
}
.vc-ps-saturation-wrap .vc-saturation-circle {
    width: 12px;
    height: 12px;
}

.vc-ps-alpha-wrap {
    margin-top: 10px;
    width: 256px;
    height: 20px;
    position: relative;

    .vc-alpha {
        border: none;
        border-radius: 4px;

        .vc-alpha-checkboard-wrap,
        .vc-alpha-gradient {
            border-radius: inherit;
        }

        .vc-alpha-picker {
            cursor: pointer;
            width: 7px;
            border-radius: 2px;
            height: 24px;
            box-shadow: 0 0 2px rgba(0, 0, 0, .7);
            background: #fff;
            margin-top: -2px;
            margin-left: -4px;
        }
    }
}

.vc-ps-hue-wrap {
    position: relative;
    height: 256px;
    width: 20px;
    margin-left: 10px;

    .vc-hue {
        border-radius: 4px;
        border: none;

        .vc-hue-picker {
            cursor: pointer;
            width: 24px;
            border-radius: 2px;
            height: 7px;
            box-shadow: 0 0 2px rgba(0, 0, 0, .7);
            background: #fff;
            margin-top: -4px;
            margin-left: -2px;
        }
    }

    .vc-transparent-wrap {
        bottom: -30px;
        position: absolute;
        width: 20px;
        height: 20px;
        border-radius: 4px;
        border: 1px solid #ccc;
        border-color: var(--input-border-color);

        .vc-checkerboard {
            border-radius: inherit;
            cursor: pointer;

            &::after {
                content: "";
                position: absolute;
                display: block;
                top: -5px;
                bottom: -5px;
                left: calc(50% - 1.5px);
                width: 3px;
                transform: rotate(45deg);
                border-radius: 2px;
                background-color: red;
            }
        }
    }
}
.vc-ps-hue-pointer {
    position: relative;
}
.vc-ps-hue-pointer--left,
.vc-ps-hue-pointer--right {
    position: absolute;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 5px 0 5px 8px;
    border-color: transparent transparent transparent #555;
}
.vc-ps-hue-pointer--left:after,
.vc-ps-hue-pointer--right:after {
    content: "";
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 4px 0 4px 6px;
    border-color: transparent transparent transparent #fff;
    position: absolute;
    top: 1px;
    left: 1px;
    transform: translate(-8px, -5px);
}
.vc-ps-hue-pointer--left {
    transform: translate(-13px, -4px);
}
.vc-ps-hue-pointer--right {
    transform: translate(20px, -4px) rotate(180deg);
}

.vc-ps-controls {
    width: 180px;
    margin-left: 10px;
    display: flex;
}
.vc-ps-controls__disable-fields {
    width: auto;
}

.vc-ps-actions {
    margin-left: 20px;
    flex: 1;

    .btn {
        width: 100%;
        margin-bottom: 5px;
        height: 26px;
        line-height: 24px;
        padding: 0 !important;

        &.btn-white {
            background: white;
        }
    }
}

.vc-ps-previews {
    width: 60px;
}
.vc-ps-previews__swatches {
    margin-bottom: 2px;
    margin-top: 1px;
    position: relative;
}
.vc-ps-previews__pr-color {
    height: 34px;
    box-shadow: inset 1px 0 0 rgba(0, 0, 0, 0.5), inset -1px 0 0 rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(0, 0, 0, 0.5);
    z-index: 1;
    position: relative;
}
.vc-ps-previews__label {
    font-size: 14px;
    color: #000;
    text-align: center;
}

.vc-ps-fields {
    padding-top: 5px;
    padding-bottom: 9px;
    width: 80px;
    position: relative;
    text-align: left;
}
.vc-ps-fields .vc-input__input {
    margin-left: 25%;
    min-width: initial !important;
    width: 75%;
    height: 20px;
    padding: 2px;
    line-height: 20px;
    font-size: 10pt;
    margin-bottom: 5px;
    margin-right: 10px;
    border-width: 1px;
    border-style: solid;
    border-color: var(--input-border-color);
}
.vc-ps-fields .vc-input__label, .vc-ps-fields .vc-input__desc {
    top: 0;
    text-transform: uppercase;
    font-size: 13px;
    height: 18px;
    line-height: 22px;
    position: absolute;
}
.vc-ps-fields .vc-input__label {
    left: 0;
    width: 34px;
}
.vc-ps-fields .vc-input__desc {
    right: -5px;
    width: 0;
}

.vc-ps-fields__divider {
    height: 5px;
}

.vc-ps-fields__hex .vc-input__input {
    width: 100%;
    padding: 4px;
    height: 24px;
    line-height: 24px;
}
.vc-ps-fields__hex .vc-input__label {
    position: absolute;
    top: 0;
    left: 0;
    width: 14px;
    text-transform: uppercase;
    font-size: 13px;
    height: 18px;
    line-height: 22px;
}

.vc-sketch-presets {
    width: 286px;
}

.vc-sketch-presets-color {
    border-radius: 4px;
    overflow: hidden;
    position: relative;
    display: inline-block;
    margin: 0 8px 8px 0;
    vertical-align: top;
    cursor: pointer;
    width: 20px;
    height: 20px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .15);
}

.vc-sketch-presets-color .vc-checkerboard {
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, .15);
    border-radius: 3px;
}

.vc-sketch__disable-alpha .vc-sketch-color-wrap {
    height: 10px;
}
</style>
